import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, Edit, Trash2, Clock, User } from 'lucide-react';
import { clientNotesService } from '../../services/api';
import ConfirmDialog from '../common/ConfirmDialog';

const NOTE_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'call', label: 'Phone Call' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'intake', label: 'Intake' },
  { value: 'other', label: 'Other' },
];

const ClientNotesSection = ({ clientId, onError, onSuccess }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '', note_type: 'general' });
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning', confirmText: 'Confirm' });

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await clientNotesService.getNotes(clientId);
      setNotes(response.data?.notes || []);
    } catch (err) {
      console.error('Error fetching client notes:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.content.trim()) return;
    setSaving(true);
    try {
      if (editingNote) {
        await clientNotesService.updateNote(clientId, editingNote.id, formData);
        onSuccess?.('Note updated');
      } else {
        await clientNotesService.createNote(clientId, formData);
        onSuccess?.('Note created');
      }
      setShowForm(false);
      setEditingNote(null);
      setFormData({ title: '', content: '', note_type: 'general' });
      fetchNotes();
    } catch (err) {
      onError?.(err.response?.data?.detail || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setFormData({ title: note.title || '', content: note.content, note_type: note.note_type });
    setShowForm(true);
  };

  const handleDelete = (noteId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Note',
      message: 'Delete this note?',
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await clientNotesService.deleteNote(clientId, noteId);
          onSuccess?.('Note deleted');
          fetchNotes();
        } catch (err) {
          onError?.(err.response?.data?.detail || 'Failed to delete note');
        }
      },
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingNote(null);
    setFormData({ title: '', content: '', note_type: 'general' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold flex items-center">
          <FileText className="h-5 w-5 mr-2 text-yellow-600" />
          Notes
          {notes.length > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">({notes.length})</span>
          )}
        </h2>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center text-sm text-yellow-600 hover:text-yellow-700 font-medium"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Note
          </button>
        )}
      </div>

      {/* Note Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="space-y-3">
            <div className="flex gap-3">
              <input
                type="text"
                placeholder="Title (optional)"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
              <select
                value={formData.note_type}
                onChange={(e) => setFormData(prev => ({ ...prev, note_type: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              >
                {NOTE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <textarea
              placeholder="Write your note..."
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              rows={4}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !formData.content.trim()}
                className="px-4 py-1.5 text-sm bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingNote ? 'Update' : 'Add Note'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Notes List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
        </div>
      ) : notes.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FileText className="h-10 w-10 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">No notes yet</p>
          <p className="text-xs text-gray-400 mt-1">Add a note to track conversations and observations</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map(note => (
            <div key={note.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {note.title && (
                    <h4 className="font-medium text-gray-900 mb-1">{note.title}</h4>
                  )}
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.content}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                      {NOTE_TYPES.find(t => t.value === note.note_type)?.label || note.note_type}
                    </span>
                    {note.user && (
                      <span className="flex items-center">
                        <User className="h-3 w-3 mr-1" />
                        {note.user.first_name} {note.user.last_name}
                      </span>
                    )}
                    <span className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {formatDate(note.created_at)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <button
                    onClick={() => handleEdit(note)}
                    className="p-1.5 text-gray-400 hover:text-yellow-600 rounded"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(note.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        confirmText={confirmDialog.confirmText}
      />
    </div>
  );
};

export default ClientNotesSection;
